/**
 * Hyperliquid WebSocket adapter.
 *
 * Subscribes to the `activeAssetCtx` channel which streams real-time
 * mark price and funding rate for a perpetual.
 */

import type { ExchangeWsAdapter } from "../live-pricing.types";

const WS_URL = "wss://api.hyperliquid.xyz/ws";
const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];

function toCoin(symbol: string): string {
  const upper = symbol.toUpperCase();
  return upper.endsWith("USDT") ? upper.slice(0, -4) : upper;
}

function normalizeSymbol(symbol: string): string {
  const coin = toCoin(symbol);
  return `${coin}USDT`;
}

interface HlAssetCtx {
  funding?: string;
  oraclePx?: string;
  markPx?: string;
  midPx?: string;
}

interface HlWsMsg {
  channel?: string;
  data?: {
    coin?: string;
    ctx?: HlAssetCtx;
  };
}

export const hyperliquidWsAdapter: ExchangeWsAdapter = {
  subscribe(symbol, onUpdate, onError) {
    const coin = toCoin(symbol);
    const normalized = normalizeSymbol(symbol);

    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;

    function connect() {
      if (stopped) return;
      ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        ws?.send(
          JSON.stringify({
            method: "subscribe",
            subscription: { type: "activeAssetCtx", coin },
          })
        );
      };

      ws.onmessage = (e: MessageEvent<string>) => {
        try {
          const msg = JSON.parse(e.data) as HlWsMsg;
          if (msg.channel !== "activeAssetCtx") return;
          if (msg.data?.coin?.toUpperCase() !== coin) return;

          const ctx = msg.data.ctx;
          if (!ctx) return;

          const markPrice = Number.parseFloat(ctx.markPx ?? "0");
          if (markPrice <= 0) return;

          const indexPrice = Number.parseFloat(
            ctx.oraclePx ?? ctx.midPx ?? String(markPrice)
          );
          const fundingRate = Number.parseFloat(ctx.funding ?? "0");

          attempt = 0;
          onUpdate({
            symbol: normalized,
            exchange: "hyperliquid",
            markPrice,
            indexPrice,
            fundingRate,
            nextFundingTime: 0,
            timestamp: Date.now(),
          });
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        // followed by close
      };

      ws.onclose = () => {
        if (stopped) return;
        const delay =
          RECONNECT_DELAYS_MS[
            Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
          ];
        attempt++;
        if (attempt > RECONNECT_DELAYS_MS.length) {
          onError?.(
            new Error(`Hyperliquid WS closed after ${attempt} attempts`)
          );
          return;
        }
        setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      stopped = true;
      ws?.close();
      ws = null;
    };
  },
};
